import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollReveal } from '../../../hooks/useScrollReveal';

const SERVICES = [
  {
    id: 'furnace',
    icon: 'ri-fire-line',
    title: 'Furnace & Heating',
    desc: 'Expert installation, emergency repair, and seasonal maintenance for high-efficiency gas furnaces. Oil-to-gas conversions available throughout the GTA.',
    tags: ['Installation', 'Repair', 'Maintenance', '24/7 Emergency'],
    img: 'https://public.readdy.ai/ai/img_res/1dc51721baad3d96c23ace4a8b393b9f.jpg',
  },
  {
    id: 'ac',
    icon: 'ri-temp-cold-line',
    title: 'Air Conditioning',
    desc: 'Central AC installation, precision tune-ups, and same-day repair for all major brands. Stay consistently cool through every GTA summer.',
    tags: ['Installation', 'Tune-Up', 'Same-Day Repair'],
    img: 'https://readdy.ai/api/search-image?query=sleek%20premium%20central%20air%20conditioning%20outdoor%20unit%20luxury%20modern%20home%20beautiful%20landscaping%20green%20garden%20summer%20light%20elegant%20residential%20HVAC%20installation%20GTA%20Ontario&width=800&height=560&seq=svc-ac-v3&orientation=landscape',
  },
  {
    id: 'iaq',
    icon: 'ri-wind-line',
    title: 'Indoor Air Quality',
    desc: 'Lennox PureAir™, HEPA filtration, HRVs, whole-home humidifiers, and UV germicidal systems. Breathe measurably cleaner air in every room.',
    tags: ['HEPA', 'HRV / ERV', 'UV Systems', 'Humidifiers'],
    img: 'https://readdy.ai/api/search-image?query=immaculate%20luxury%20home%20interior%20bright%20natural%20light%20clean%20fresh%20air%20quality%20premium%20open%20plan%20living%20room%20white%20marble%20sophisticated%20modern%20Canadian%20architecture%20healthy%20living&width=800&height=560&seq=svc-iaq-v3&orientation=landscape',
  },
  {
    id: 'duct',
    icon: 'ri-refresh-line',
    title: 'Duct Cleaning',
    desc: 'Professional duct cleaning removes allergens, dust, and debris — restoring system efficiency and delivering noticeably healthier indoor air quality.',
    tags: ['Allergen Removal', 'Efficiency', 'All Brands'],
    img: 'https://readdy.ai/api/search-image?query=professional%20HVAC%20technician%20in%20clean%20uniform%20working%20inside%20bright%20luxury%20home%20residential%20duct%20cleaning%20service%20professional%20clean%20organized%20equipment%20premium%20residential%20service&width=800&height=560&seq=svc-duct-v3&orientation=landscape',
  },
];

export default function ServicesOverview() {
  const [hovered, setHovered] = useState<string | null>(null);
  const { ref, visible } = useScrollReveal<HTMLElement>();

  return (
    <section ref={ref} className="bg-luxury-cream py-28 lg:py-36 overflow-hidden">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-14">

        {/* Section Header */}
        <div
          className={`flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-0.5 bg-luxury-gold-dark" />
              <span className="font-caps text-[10px] tracking-[0.3em] uppercase text-luxury-text-muted">What We Do</span>
            </div>
            <h2 className="font-serif text-5xl lg:text-7xl text-luxury-text-dark leading-[1.0]">
              Comprehensive HVAC<br />
              <em className="not-italic text-luxury-gold-dark">Solutions</em>
            </h2>
          </div>
          <div className="flex flex-col items-start lg:items-end gap-3">
            <p className="font-sans text-sm text-luxury-text-muted max-w-xs lg:text-right leading-relaxed">
              Four core service pillars — all backed by T.S.S.A. certification, transparent pricing, and 20+ years of GTA expertise.
            </p>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 font-caps text-xs tracking-[0.15em] uppercase text-luxury-gold-dark border-b border-luxury-gold-dark/40 pb-0.5 hover:border-luxury-gold-dark transition-colors cursor-pointer whitespace-nowrap"
            >
              <span>View All Services</span>
              <i className="ri-arrow-right-line text-sm" />
            </Link>
          </div>
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-4">
          {SERVICES.map((s, i) => (
            <div
              key={s.id}
              onMouseEnter={() => setHovered(s.id)}
              onMouseLeave={() => setHovered(null)}
              className={`group relative bg-white rounded-2xl overflow-hidden border cursor-pointer transition-all duration-500 shadow-card ${
                hovered === s.id ? 'border-luxury-gold/50 -translate-y-2 shadow-luxury-md' : 'border-gray-100 hover:border-luxury-gold/25'
              } ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}
            >
              {/* Image */}
              <div className="w-full h-48 overflow-hidden relative">
                <img
                  src={s.img}
                  alt={s.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                <div className="absolute top-4 right-4">
                  <span className="font-serif text-white/20 text-4xl font-bold leading-none">0{i + 1}</span>
                </div>
                <div className="absolute bottom-4 left-4 w-10 h-10 flex items-center justify-center rounded-xl bg-luxury-dark/85 backdrop-blur-sm border border-luxury-gold/30">
                  <i className={`${s.icon} text-luxury-gold text-base`} />
                </div>
              </div>

              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-serif text-luxury-text-dark text-xl font-semibold mb-3 leading-snug group-hover:text-luxury-gold-dark transition-colors">
                  {s.title}
                </h3>
                <p className="font-sans text-sm text-luxury-text-muted leading-relaxed mb-5 flex-1">{s.desc}</p>

                {/* Tags */}
                <div className="flex flex-wrap gap-1.5 mb-5">
                  {s.tags.map((tag) => (
                    <span
                      key={tag}
                      className="font-caps text-[9px] tracking-widest text-luxury-gold-dark/90 bg-luxury-gold/8 border border-luxury-gold/20 px-2.5 py-1 rounded-full whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <Link
                  to="/services"
                  className="inline-flex items-center gap-1.5 font-caps text-[10px] tracking-[0.15em] uppercase text-luxury-gold-dark hover:text-luxury-gold transition-colors cursor-pointer whitespace-nowrap mt-auto"
                >
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line text-xs group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Emergency CTA Banner */}
        <div
          className={`mt-14 relative overflow-hidden rounded-2xl transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ transitionDelay: '400ms' }}
        >
          <div
            className="absolute inset-0 rounded-2xl"
            style={{ background: 'linear-gradient(135deg, #030D1F 0%, #0D1E3A 50%, #071224 100%)' }}
          />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 55% 120% at 85% 50%, rgba(26,101,224,0.12) 0%, transparent 65%)' }} />

          <div className="relative flex flex-col lg:flex-row items-center justify-between gap-6 px-8 lg:px-12 py-9">
            <div className="text-center lg:text-left">
              <div className="flex items-center gap-2 mb-2 justify-center lg:justify-start">
                <div className="w-1.5 h-1.5 rounded-full bg-luxury-gold animate-pulse" />
                <span className="font-caps text-[9px] tracking-[0.3em] uppercase text-white">Available Around the Clock</span>
              </div>
              <h3 className="font-serif text-white text-2xl lg:text-3xl font-semibold mb-1">
                Need Emergency HVAC Service?
              </h3>
              <p className="font-sans text-sm text-white">Our technicians are on call 24/7 across the Greater Toronto Area.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <a
                href="tel:9058454009"
                className="inline-flex items-center justify-center gap-2.5 bg-luxury-gold hover:bg-luxury-gold-light text-white font-caps font-bold text-[11px] tracking-[0.15em] uppercase px-7 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap shadow-gold"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-fill text-xs" /></div>
                <span>GTA West: (905) 845-4009</span>
              </a>
              <a
                href="tel:4168913444"
                className="inline-flex items-center justify-center gap-2.5 border border-luxury-gold/45 hover:border-luxury-gold text-luxury-gold font-caps font-bold text-[11px] tracking-[0.15em] uppercase px-7 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-line text-xs" /></div>
                <span>GTA East: (416) 891-3444</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
